import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, LogIn, ShieldCheck } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { errorMessage } from '../api/client';

// Second step of every MFA-protected login flow. Renders a 4-digit OTP
// entry pane below a shield header. Given `challenge` from the
// step-1 response — { userId, channel, to, otpExpiresInSec } — this
// component:
//   • submits the OTP via useAuth().verifyLoginOtp,
//   • calls onSuccess() when the JWT is issued,
//   • exposes a "Change credentials" button that calls onBack().
//
// Props:
//   challenge  — object returned by step 1 (login/signIn/portalLogin/etc)
//   onSuccess  — () => void   called after JWT is stored
//   onBack     — () => void   called when the user wants to redo step 1
//   onResend   — optional async () => void   re-triggers step 1 to get a
//                fresh OTP (typically the caller re-runs the same
//                signIn/login call with the stored credentials).
export default function LoginOtpStep({ challenge, onSuccess, onBack, onResend }) {
  const { verifyLoginOtp } = useAuth();
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  async function submit(e) {
    e?.preventDefault?.();
    setErr('');
    if (otp.length < 4) { setErr('Enter the 4-digit code we sent.'); return; }
    setSubmitting(true);
    try {
      await verifyLoginOtp(challenge.userId, otp);
      onSuccess?.();
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setSubmitting(false);
    }
  }

  async function resend() {
    if (!onResend) return;
    setErr('');
    setResending(true);
    try {
      await onResend();
      setOtp('');
      inputRef.current?.focus();
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setResending(false);
    }
  }

  const channel = challenge?.channel === 'email' ? 'email' : 'phone';

  return (
    <>
      <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 'var(--s-2)' }}>
        <ShieldCheck size={18} color="var(--brand-700)" />
        <h2 style={{ margin: 0 }}>Verify it's you</h2>
      </div>
      <div className="auth-sub" style={{ marginBottom: 'var(--s-3)' }}>
        We sent a 4-digit code to your {channel} <strong>{challenge?.to}</strong>.
        Enter it below to finish signing in.
      </div>

      <form onSubmit={submit}>
        <div className="field">
          <label>Verification code</label>
          <input
            ref={inputRef}
            className="input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D+/g, ''))}
            placeholder="1111"
            style={{ letterSpacing: 4, fontSize: 18, textAlign: 'center' }}
            required
          />
        </div>

        {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

        <button type="submit" className="btn btn-primary btn-block" disabled={submitting || resending}>
          {submitting ? <span className="spinner" /> : <LogIn size={16} />}
          {submitting ? 'Verifying…' : 'Verify & sign in'}
        </button>
      </form>

      <div className="row between" style={{ marginTop: 'var(--s-4)' }}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--brand-700)' }}
            className="text-sm"
          >
            <ArrowLeft size={14} /> Change credentials
          </button>
        )}
        {onResend && (
          <button
            type="button"
            onClick={resend}
            disabled={submitting || resending}
            style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--brand-700)' }}
            className="text-sm"
          >
            {resending ? 'Resending…' : (<>Resend code <ArrowRight size={14} /></>)}
          </button>
        )}
      </div>
    </>
  );
}
